import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { LocalService } from '../services/local.service';

/**
 * Auth Guard - Protects routes that require authentication
 * Redirects to login if user is not authenticated
 * Platform-aware: Works with both SSR and browser environments
 */
export const authGuard: CanActivateFn = (route, state) => {
  const platformId = inject(PLATFORM_ID);
  const authService = inject(AuthService);
  const router = inject(Router);
  const localService = inject(LocalService);

  // During SSR, browser storage is unavailable. Let client-side guard decide.
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  if (authService.isLoggedIn()) {
    return true;
  }

  // Store the attempted URL for redirecting after login (platform-safe)
  localService.saveData('redirectUrl', state.url);

  router.navigate(['/auth/login']);
  return false;
};

/**
 * No-Auth Guard - Prevents authenticated users from accessing auth pages
 * Redirects to home if user is already authenticated
 */
export const noAuthGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isLoggedIn()) {
    return true;
  }

  router.navigate(['/']);
  return false;
};

/**
 * Role Guard - Restricts a route to users whose role satisfies the predicate.
 * Usage: canActivate: [roleGuard(r => r.canManageUsers())]
 */
export const roleGuard =
  (allowed: (auth: AuthService) => boolean): CanActivateFn =>
  () => {
    const platformId = inject(PLATFORM_ID);
    const authService = inject(AuthService);
    const router = inject(Router);

    // During SSR, browser storage is unavailable. Let client-side guard decide.
    if (!isPlatformBrowser(platformId)) {
      return true;
    }

    if (allowed(authService)) {
      return true;
    }

    router.navigate(['/admin']);
    return false;
  };
